<template>
  <v-card>
    <v-card-title>
      <span>{{ $t('reports_ui.fleet_health_report.fleet_health_trend') }}</span>
      <info-modal>
        <v-card-title>
          {{ $t('reports_ui.fleet_health_report.fleet_health_trend') }}
        </v-card-title>
        <v-card-text v-html="$t('reports_ui.fleet_health_report.modal_info.fleet_health_trend')" />
      </info-modal>
    </v-card-title>
    <v-card-text>
      <v-simple-table v-if="fleetHealthTrend" dense class="mb-3">
        <thead>
          <tr>
            <th></th>
            <th>{{ $t('reports_ui.fleet_health_report.fleet_health') }}</th>
            <th>{{ $t('reports_ui.fleet_health_report.fleet_health_num_healthy') }}</th>
            <th>{{ $t('reports_ui.fleet_health_report.fleet_health_num_unhealthy') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ fleetHealthTrend.startDate }}</td>
            <td>{{ fleetHealthTrend.startHealthyPct }}%</td>
            <td>{{ fleetHealthTrend.startHealthyCount }}</td>
            <td>{{ fleetHealthTrend.startUnhealthyCount }}</td>
          </tr>
          <tr>
            <td>{{ fleetHealthTrend.endDate }}</td>
            <td>{{ fleetHealthTrend.endHealthyPct }}%</td>
            <td>{{ fleetHealthTrend.endHealthyCount }}</td>
            <td>{{ fleetHealthTrend.endUnhealthyCount }}</td>
          </tr>
          <tr>
            <td>{{ $t('reports_ui.fleet_health_report.fleet_health_difference') }}</td>
            <td>
              <span :class="fleetHealthTrend.diffHealthyPctStyle">
                {{ fleetHealthTrend.diffHealthyPct }}%
              </span>
            </td>
            <td>{{ fleetHealthTrend.diffHealthyCount }}</td>
            <td>{{ fleetHealthTrend.diffUnhealthyCount }}</td>
          </tr>
        </tbody>
      </v-simple-table>
      <c3-chart :height="250" :data="chartData" :config="chartConfig" type='area'/>
      <div class="text-center text-muted" style="margin-top:-1.5rem">
        {{ $t('reports_ui.fleet_health_report.fleet_health_over_time') }}
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import C3Chart from '@/components/shared/chart/C3Chart.vue'
import InfoModal from '@/components/shared/InfoModal'
import { filter, map, minBy, isEmpty, keys, get, round, first, last } from 'lodash'

export default {
  name: 'FleetHealthTrends.vue',
  components: {
    InfoModal,
    C3Chart
  },
  props: {
    customerReport: {
      type: Object
    }
  },
  computed: {
    chartConfig () {
      return {
        axis: {
          x: {
            label: {
              text: 'Date'
            },
            type: 'timeseries',
            tick: {
              format: '%Y-%m-%d',
              centered: true
            }
          },
          y: {
            label: {
              text: this.$t('percentage_of_healthy_vehicles')
            },
            tick: {
              values: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
              format: function (d) {
                return round(d, 1)
              },
              culling: true
            },
            min: this.lowestHealth >= 60 ? 60 : 0,
            max: 100
          }
        },
        line: {
          connectNull: true
        },
        legend: {
          hide: true
        }
      }
    },
    /**
     * Format the chart data needed for the graph.
     * @returns Object
     */
    chartData () {
      const dateColumn = keys(this.customerReport.insights_dashboard_history)
      dateColumn.unshift('date')
      const percentColumn = map(this.customerReport.insights_dashboard_history, x => get(x, 'percent_healthy', null))
      percentColumn.unshift(this.$t('percent'))
      return {
        x: 'date',
        columns: [dateColumn, percentColumn],
        onclick: function (d) {
          // TODO: if we create an endpoint for customer insight history, we could redirect to it here.
        }
      }
    },
    /**
     * Get history for all dates that have it, filtering out those that don't.
     */
    filteredHistory () {
      return filter(this.customerReport.insights_dashboard_history, x => !isEmpty(x))
    },
    /**
     * Find the lowest health percent value for the purposes of setting the graph bounds
     * @returns {number}
     */
    lowestHealth () {
      const lowest = minBy(this.filteredHistory, x => x.percent_healthy)
      if (lowest) {
        return Math.floor(lowest.percent_healthy)
      }
      return 0
    },
    /**
     * Compute overall trend stats for the table, displaying the differences between the first and last available
     * pieces of data in the report window (via filteredHistory)
     */
    fleetHealthTrend () {
      const formatPlusMinus = (x) => (x >= 0 ? '+' : '') + x
      if (this.filteredHistory.length > 1) {
        const start = first(this.filteredHistory)
        const end = last(this.filteredHistory)
        const diffHealthyPct = Math.floor(end.percent_healthy) - Math.floor(start.percent_healthy)
        let diffHealthyPctStyle = ''
        if (diffHealthyPct > 0) {
          diffHealthyPctStyle = 'success--text'
        } else if (diffHealthyPct < 0) {
          diffHealthyPctStyle = 'danger--text'
        }
        return {
          diffHealthyPct: formatPlusMinus(diffHealthyPct),
          diffHealthyPctStyle: diffHealthyPctStyle,
          diffHealthyCount: formatPlusMinus(end.num_healthy - start.num_healthy),
          diffUnhealthyCount: formatPlusMinus(end.num_unhealthy - start.num_unhealthy),
          startDate: start.date,
          startHealthyPct: Math.floor(start.percent_healthy),
          startHealthyCount: start.num_healthy,
          startUnhealthyCount: start.num_unhealthy,
          endDate: end.date,
          endHealthyPct: Math.floor(end.percent_healthy),
          endHealthyCount: end.num_healthy,
          endUnhealthyCount: end.num_unhealthy
        }
      }
      return undefined
    }
  }
}
</script>

<style scoped>
</style>
